import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Search, ChevronDown } from "./icons";
import "./Select.css";

const labelText = (label) =>
  typeof label === "string" || typeof label === "number" ? String(label) : "";

const Select = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select option",
  error,
  helperText,
  disabled = false,
  fullWidth = true,
  className = "",
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const focusMenuRef = useRef(false);
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const valueId = `${baseId}-value`;
  const menuId = `${baseId}-menu`;
  const messageId = `${baseId}-message`;
  const hasMessage = error ? error !== true : Boolean(helperText);

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  // Close when clicking outside; only listens while the menu is open.
  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = useMemo(() => {
    const query = search.toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      labelText(option.label).toLowerCase().includes(query),
    );
  }, [options, search]);

  const handleSelect = (option) => {
    onChange?.({
      target: {
        value: option.value,
      },
    });

    close();
    triggerRef.current?.focus();
  };

  // Escape closes; arrow keys move between the search box and the options.
  const handleMenuKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    const items = Array.from(
      menuRef.current?.querySelectorAll("input, .ui-select-option") || [],
    );
    if (!items.length) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const next = current === -1 ? 0 : (current + step + items.length) % items.length;
    items[next].focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (open) {
        menuRef.current?.querySelector("input")?.focus();
      } else {
        focusMenuRef.current = true;
        setOpen(true);
      }
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close();
    }
  };

  // Tabbing out of the whole control closes the menu. A blur without a new
  // focus target (e.g. Safari clicking a button) is left to the outside-click
  // listener so it can't close the menu before an option's click lands.
  const handleBlur = (event) => {
    if (
      open &&
      event.relatedTarget &&
      !selectRef.current?.contains(event.relatedTarget)
    ) {
      close();
    }
  };

  // Opening with ArrowDown moves focus into the menu; a click keeps it on the
  // trigger (no on-screen keyboard popping up on mobile).
  useEffect(() => {
    if (open && focusMenuRef.current) {
      focusMenuRef.current = false;
      menuRef.current?.querySelector("input")?.focus();
    }
  }, [open]);

  return (
    <div
      ref={selectRef}
      className={`ui-select-container ${
        fullWidth ? "ui-full-width" : ""
      } ${className}`}
      onBlur={handleBlur}
      {...props}
    >
      {label && (
        <label className="ui-select-label" id={labelId}>
          {label}
        </label>
      )}

      <div
        className={`ui-select-dropdown ${
          error ? "error" : ""
        } ${disabled ? "disabled" : ""}`}
      >
        <button
          ref={triggerRef}
          type="button"
          className="ui-select-trigger"
          disabled={disabled}
          aria-haspopup="true"
          aria-expanded={open && !disabled}
          aria-controls={open && !disabled ? menuId : undefined}
          aria-labelledby={label ? `${labelId} ${valueId}` : undefined}
          aria-describedby={hasMessage ? messageId : undefined}
          onClick={() => (open ? close() : setOpen(true))}
          onKeyDown={handleTriggerKeyDown}
        >
          <span id={valueId} className={selectedOption ? "" : "placeholder"}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>

          <ChevronDown size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>

        {open && !disabled && (
          <div
            className="ui-select-menu"
            id={menuId}
            ref={menuRef}
            onKeyDown={handleMenuKeyDown}
          >
            <div className="ui-select-search">
              <Search size={16} strokeWidth={1.8} aria-hidden="true" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                aria-label="Search"
              />
            </div>

            <div className="ui-select-options">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, i) => (
                  <button
                    type="button"
                    key={`${option.value}-${i}`}
                    className={`ui-select-option ${
                      option.value === value ? "selected" : ""
                    }`}
                    aria-pressed={option.value === value}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <div className="ui-select-empty" role="status">No results</div>
              )}
            </div>
          </div>
        )}
      </div>

      {error ? (
        <span className="ui-select-error" id={hasMessage ? messageId : undefined}>
          {error}
        </span>
      ) : helperText ? (
        <span className="ui-select-helper" id={messageId}>{helperText}</span>
      ) : null}
    </div>
  );
};

export default Select;
